import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Entity types
const ENTITY_TYPES = [
  { value: 'private', label_hr: 'Privatni iznajmljivač', label_en: 'Private Landlord' },
  { value: 'trade', label_hr: 'Obrt', label_en: 'Sole Proprietorship' },
  { value: 'company', label_hr: 'Trgovačko društvo (d.o.o.)', label_en: 'Limited Company (Ltd)' }
];

const PropertyOwnersManager = () => {
  const [owners, setOwners] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [configuredRoomIds, setConfiguredRoomIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOwner, setEditingOwner] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    entity_type: 'private',
    company: '',
    oib: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Hrvatska',
    assigned_rooms: []
  });

  useEffect(() => {
    fetchOwners();
    fetchConfiguredRooms();
  }, []);

  const fetchOwners = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/owners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwners(response.data);
    } catch (error) {
      console.error('Error fetching owners:', error);
      toast.error('Failed to load owners');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfiguredRooms = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Get all room media - only rooms with media records should be selectable
      const mediaResponse = await axios.get(`${API}/admin/rooms/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Only show rooms that have room_media entries (these are the actual configured rooms)
      const allRooms = mediaResponse.data || [];
      setRooms(allRooms);
      
      // Store configured room IDs for reference
      const roomIds = allRooms.map(room => room.room_id);
      setConfiguredRoomIds(roomIds);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoomToggle = (roomId) => {
    setFormData(prev => {
      const currentRooms = prev.assigned_rooms || [];
      if (currentRooms.includes(roomId)) {
        return { ...prev, assigned_rooms: currentRooms.filter(id => id !== roomId) };
      } else {
        return { ...prev, assigned_rooms: [...currentRooms, roomId] };
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      entity_type: 'private',
      company: '',
      oib: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'Hrvatska',
      assigned_rooms: []
    });
    setEditingOwner(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    // Build full address string
    const fullAddress = [
      formData.address,
      formData.postal_code && formData.city ? `${formData.postal_code} ${formData.city}` : formData.city,
      formData.country
    ].filter(Boolean).join(', ');

    const submitData = {
      ...formData,
      full_address: fullAddress
    };

    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingOwner) {
        await axios.put(`${API}/admin/owners/${editingOwner.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Owner updated successfully');
      } else {
        await axios.post(`${API}/admin/owners`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Owner created successfully');
      }
      
      fetchOwners();
      resetForm();
    } catch (error) {
      console.error('Error saving owner:', error);
      toast.error('Failed to save owner');
    }
  };

  const handleEdit = (owner) => {
    setFormData({
      name: owner.name || '',
      entity_type: owner.entity_type || 'private',
      company: owner.company || '',
      oib: owner.oib || '',
      email: owner.email || '',
      phone: owner.phone || '',
      address: owner.address || '',
      city: owner.city || '',
      postal_code: owner.postal_code || '',
      country: owner.country || 'Hrvatska',
      assigned_rooms: owner.assigned_rooms || []
    });
    setEditingOwner(owner);
    setShowForm(true);
  };

  const handleDelete = async (ownerId) => {
    if (!window.confirm('Are you sure you want to delete this owner?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/owners/${ownerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Owner deleted successfully');
      fetchOwners();
    } catch (error) {
      console.error('Error deleting owner:', error);
      toast.error('Failed to delete owner');
    }
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.room_id === roomId);
    return room?.room_name || `Room ${roomId}`;
  };

  const getEntityTypeLabel = (type) => {
    const entity = ENTITY_TYPES.find(e => e.value === type);
    return entity?.label_hr || type;
  };

  // Get all assigned rooms from all owners
  const getAssignedRooms = () => {
    const assigned = new Set();
    owners.forEach(owner => {
      if (editingOwner && owner.id === editingOwner.id) return;
      (owner.assigned_rooms || []).forEach(roomId => assigned.add(roomId));
    });
    return assigned;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Vlasnici objekata / Voditelji obrade</h3>
          <p className="text-sm text-gray-500 mt-1">
            Upravljajte vlasnicima koji su odgovorni za obradu podataka gostiju (GDPR Data Controllers)
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Dodaj vlasnika
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="border-2 border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">
              {editingOwner ? 'Uredi vlasnika' : 'Dodaj novog vlasnika'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium">Tip entiteta *</Label>
                  <select
                    value={formData.entity_type}
                    onChange={(e) => handleInputChange('entity_type', e.target.value)}
                    className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                  >
                    {ENTITY_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label_hr}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">
                    {formData.entity_type === 'private' ? 'Ime i prezime *' : 'Naziv firme *'}
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder={formData.entity_type === 'private' ? 'Ivan Horvat' : 'Naziv d.o.o.'}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">OIB *</Label>
                  <Input
                    value={formData.oib}
                    onChange={(e) => handleInputChange('oib', e.target.value)}
                    placeholder="12345678901"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="vlasnik@email.com"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Telefon</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+385 99 123 4567"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="pt-2 border-t">
                <Label className="text-sm font-medium text-gray-700 mb-3 block">Adresa sjedišta</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-xs text-gray-500">Ulica i kućni broj</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder="Krešimirova 20"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Poštanski broj</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      placeholder="51000"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Grad</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Rijeka"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Država</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      placeholder="Hrvatska"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Room Assignment */}
              <div className="pt-4 border-t">
                <Label className="text-sm font-medium mb-3 block">
                  Dodijeljeni objekti
                  <span className="text-gray-500 font-normal ml-2">
                    (Odaberite za koje objekte je ovaj vlasnik odgovoran)
                  </span>
                </Label>
                {rooms.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                    <p className="font-medium">Nema konfiguriranih soba</p>
                    <p className="text-xs mt-1">Prvo konfigurirajte sobe u sekciji "Property Configurations" iznad.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                    {rooms.map(room => {
                      const isAssignedToOther = getAssignedRooms().has(room.room_id);
                      const isSelected = formData.assigned_rooms.includes(room.room_id);
                      
                      return (
                        <label
                          key={room.room_id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50' 
                              : isAssignedToOther 
                                ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed' 
                                : 'border-gray-200 hover:border-blue-300 bg-white'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => !isAssignedToOther && handleRoomToggle(room.room_id)}
                            disabled={isAssignedToOther}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {room.room_name || `Room ${room.room_id}`}
                            </p>
                            <p className="text-xs text-gray-400">ID: {room.room_id}</p>
                          </div>
                          {isAssignedToOther && (
                            <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                              Već dodijeljeno
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingOwner ? 'Spremi izmjene' : 'Dodaj vlasnika'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Odustani
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Owners List */}
      {owners.length === 0 && !showForm ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nema unesenih vlasnika</h3>
            <p className="text-gray-500 mb-4">Dodajte vlasnike objekata koji su odgovorni za obradu podataka gostiju</p>
            <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700">
              Dodaj prvog vlasnika
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {owners.map(owner => (
            <Card key={owner.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        owner.entity_type === 'company' ? 'bg-purple-100 text-purple-800' :
                        owner.entity_type === 'trade' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {getEntityTypeLabel(owner.entity_type)}
                      </span>
                      <h4 className="text-lg font-semibold text-gray-900">{owner.name}</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      {owner.oib && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                          </svg>
                          OIB: {owner.oib}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {owner.email}
                      </div>
                      {owner.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {owner.phone}
                        </div>
                      )}
                    </div>

                    {/* Full Address */}
                    {(owner.address || owner.full_address) && (
                      <div className="flex items-start gap-2 text-gray-600 mt-2 text-sm">
                        <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>
                          {owner.full_address || [
                            owner.address,
                            owner.postal_code && owner.city ? `${owner.postal_code} ${owner.city}` : owner.city,
                            owner.country
                          ].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}

                    {/* Assigned Rooms - only show rooms that exist in room_media */}
                    {owner.assigned_rooms && owner.assigned_rooms.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                          Odgovorni za objekte:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {owner.assigned_rooms
                            .filter(roomId => rooms.some(r => r.room_id === roomId))
                            .map(roomId => (
                            <span
                              key={roomId}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                            >
                              {getRoomName(roomId)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(owner)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(owner.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Box */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">GDPR Sukladnost</p>
              <p>
                Vlasnici objekata navedeni ovdje su <strong>Voditelji obrade podataka</strong> prema GDPR-u. Oni su odgovorni 
                za obradu osobnih podataka gostiju za objekte koje posjeduju. Ove informacije bit će prikazane u 
                Politici Privatnosti i Uvjetima Korištenja, a gosti će morati prihvatiti ove uvjete prije rezervacije.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PropertyOwnersManager;
